import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, X, Send, Sparkles, Pill, Loader2 } from "lucide-react";
import api, { imgUrl, eur } from "../../lib/api";
import { useSettings } from "../../context/SettingsContext";

const QUICK = ["Κάτι για ξηρό δέρμα", "Ποιο είναι το ωράριο σας;", "Προτείνετέ μου ένα αντηλιακό", "Πώς γίνεται η αποστολή;"];

function getSession() {
  let s = localStorage.getItem("aiChatSession");
  if (!s) { s = (crypto.randomUUID ? crypto.randomUUID() : String(Date.now())); localStorage.setItem("aiChatSession", s); }
  return s;
}

export default function ChatWidget() {
  const { settings } = useSettings();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const session = useRef(getSession());

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", content: `Γεια σας! 👋 Είμαι ο ψηφιακός βοηθός του ${settings?.shop_name || "φαρμακείου"}. Πώς μπορώ να σας βοηθήσω;`, suggestions: [] }]);
    }
  }, [open]); // eslint-disable-line

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", { session_id: session.current, message: msg });
      setMessages((m) => [...m, { role: "assistant", content: data.reply, suggestions: data.suggestions || [] }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Συγγνώμη, ο βοηθός δεν είναι διαθέσιμος αυτή τη στιγμή. Δοκιμάστε ξανά σε λίγο.", suggestions: [] }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* FAB */}
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="chat-fab"
        className="fixed bottom-5 right-5 z-[60] h-14 w-14 rounded-full bg-[#1E3A2F] text-white shadow-lg flex items-center justify-center hover:bg-[#111C18] transition-colors"
        aria-label="AI βοηθός"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>

      {/* Panel */}
      {open && (
        <div className="fixed bottom-24 right-5 z-[60] w-[92vw] max-w-sm h-[70vh] max-h-[560px] bg-white rounded-2xl shadow-2xl border border-[#E5E9E4] flex flex-col overflow-hidden animate-fade-up" data-testid="chat-panel">
          {/* Header */}
          <div className="bg-[#1E3A2F] text-white px-4 py-3 flex items-center gap-2">
            <span className="h-8 w-8 rounded-full bg-white/15 flex items-center justify-center"><Sparkles className="h-4 w-4" /></span>
            <div className="leading-tight">
              <p className="font-semibold text-sm">Ψηφιακός Βοηθός</p>
              <p className="text-[11px] text-[#CBD9B6]">Online · απαντά άμεσα</p>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3 bg-[#F9F8F6]">
            {messages.map((m, i) => (
              <div key={i}>
                <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm whitespace-pre-line ${m.role === "user" ? "ml-auto bg-[#1E3A2F] text-white rounded-br-sm" : "mr-auto bg-white border border-[#E5E9E4] text-[#2a3a33] rounded-bl-sm"}`}>
                  {m.content}
                </div>
                {m.suggestions?.length > 0 && (
                  <div className="mt-2 space-y-2">
                    {m.suggestions.map((p) => (
                      <Link key={p.id} to={`/product/${p.id}`} onClick={() => setOpen(false)} data-testid={`chat-suggestion-${p.id}`} className="flex items-center gap-2 bg-white border border-[#E5E9E4] rounded-xl p-2 hover:border-[#1E3A2F] transition-colors">
                        <div className="h-10 w-10 rounded-lg overflow-hidden bg-[#E8F0E1] shrink-0 flex items-center justify-center">
                          {p.image ? <img src={imgUrl(p.image)} alt="" className="h-full w-full object-cover" /> : <Pill className="h-4 w-4 text-[#1E3A2F]/40" />}
                        </div>
                        <span className="text-xs text-[#1E3A2F] flex-1 line-clamp-2">{p.name}</span>
                        <span className="text-xs font-semibold text-[#1E3A2F]">{eur(p.price)}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="mr-auto bg-white border border-[#E5E9E4] rounded-2xl rounded-bl-sm px-3 py-2 flex items-center gap-1">
                <span className="h-1.5 w-1.5 bg-[#536D61] rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="h-1.5 w-1.5 bg-[#536D61] rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="h-1.5 w-1.5 bg-[#536D61] rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            )}
            {messages.length <= 1 && !loading && (
              <div className="flex flex-wrap gap-2 pt-1">
                {QUICK.map((q) => (
                  <button key={q} onClick={() => send(q)} className="text-xs px-3 py-1.5 rounded-full bg-white border border-[#E5E9E4] text-[#1E3A2F] hover:bg-[#E8F0E1]">{q}</button>
                ))}
              </div>
            )}
          </div>

          {/* Input */}
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-[#E5E9E4] flex items-center gap-2 bg-white">
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Γράψτε το μήνυμά σας..."
              className="flex-1 px-3 py-2.5 rounded-full border border-[#E5E9E4] text-sm outline-none focus:border-[#1E3A2F]"
            />
            <button type="submit" disabled={loading || !input.trim()} data-testid="chat-send" className="h-10 w-10 shrink-0 rounded-full bg-[#1E3A2F] text-white flex items-center justify-center hover:bg-[#111C18] disabled:opacity-40">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </form>
          <p className="text-[10px] text-center text-[#9aa89f] pb-2">Οι πληροφορίες δεν αποτελούν ιατρική συμβουλή.</p>
        </div>
      )}
    </>
  );
}
